import { useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from './Button'
import { useAuth } from '../hooks/useAuth'

interface LoginModalProps {
  isOpen: boolean
  onClose: () => void
}

export function LoginModal({ isOpen, onClose }: LoginModalProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { login } = useAuth()

  if (!isOpen) return null

  const handleLogin = async () => {
    try {
      setIsLoading(true)
      setError(null)
      // OAuth2 login will redirect to Keycloak
      await login()
      // Note: onClose() won't execute because login() redirects the page
    } catch (err) {
      setError('Login failed. Please try again.')
      console.error('Login error:', err)
      setIsLoading(false)
    }
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={handleOverlayClick}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Sign In</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="space-y-6">
          <p className="text-gray-600 text-center">
            Sign in to access your dashboard, view orders, and manage your self-hosting services.
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              onClick={handleLogin}
              disabled={isLoading}
              className="w-full"
              size="lg"
            >
              {isLoading ? 'Redirecting...' : 'Login'}
            </Button>

            <Button
              onClick={handleLogin}
              disabled={isLoading}
              variant="outline"
              className="w-full"
              size="lg"
            >
              Register
            </Button>
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-500">
              Secure authentication with NickFixIt Account
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            By continuing, you agree to our Terms of Service and Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  )
}